import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  useAuth,
} from '@aaas/shared';
import emailService from '../services/email.service';
import { sanitizeHtml } from '../utils/sanitize-html';
import { TiptapEmailEditor } from './TiptapEmailEditor';
import type { EmailTemplate } from '../types';

interface EmailTemplateFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  template?: EmailTemplate | null;
  onSuccess: () => void;
}

export function EmailTemplateFormDialog({
  open,
  onClose,
  mode,
  template,
  onSuccess,
}: EmailTemplateFormDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace } = useAuth();
  const isEdit = mode === 'edit' && !!template;
  const [name, setName] = useState(isEdit ? template.name : '');
  const [category, setCategory] = useState(isEdit ? template.category || '' : '');
  const [subject, setSubject] = useState(isEdit ? template.subject : '');
  const [bodyHtml, setBodyHtml] = useState(isEdit ? template.body_html : '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bodyTab, setBodyTab] = useState<'edit' | 'preview'>('edit');
  const [showVariableRef, setShowVariableRef] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!name.trim()) {
      setError(t('common.validation.nameRequired'));
      return;
    }
    if (!subject.trim()) {
      setError(t('emailTemplate.form.errors.subjectRequired'));
      return;
    }
    if (!bodyHtml.trim()) {
      setError(t('emailTemplate.form.errors.bodyRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        await emailService.createTemplate(currentWorkspace.id, token, {
          name: name.trim(),
          category: category || undefined,
          subject: subject.trim(),
          body_html: bodyHtml,
        });
      } else if (template) {
        await emailService.updateTemplate(template.id, currentWorkspace.id, token, {
          name: name.trim(),
          category: category || undefined,
          subject: subject.trim(),
          body_html: bodyHtml,
        });
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const variableGroups = [
    {
      title: t('emailTemplate.form.groups.candidate'),
      vars: [
        { name: '{{candidate_name}}', desc: t('emailTemplate.form.vars.candidateName') },
        { name: '{{candidate_email}}', desc: t('emailTemplate.form.vars.candidateEmail') },
      ],
    },
    {
      title: t('emailTemplate.form.groups.job'),
      vars: [
        { name: '{{job_title}}', desc: t('emailTemplate.form.vars.jobTitle') },
        { name: '{{job_code}}', desc: t('emailTemplate.form.vars.jobCode') },
      ],
    },
    {
      title: t('emailTemplate.form.groups.interview'),
      vars: [
        { name: '{{interview_date}}', desc: t('emailTemplate.form.vars.interviewDate') },
        { name: '{{interview_time}}', desc: t('emailTemplate.form.vars.interviewTime') },
        { name: '{{interview_link}}', desc: t('emailTemplate.form.vars.interviewLink') },
        { name: '{{interview_address}}', desc: t('emailTemplate.form.vars.interviewAddress') },
      ],
    },
    {
      title: t('emailTemplate.form.groups.event'),
      vars: [
        { name: '{{event_title}}', desc: t('emailTemplate.form.vars.eventTitle') },
        { name: '{{event_type}}', desc: t('emailTemplate.form.vars.eventType') },
        { name: '{{event_end_time}}', desc: t('emailTemplate.form.vars.eventEndTime') },
      ],
    },
    {
      title: t('emailTemplate.form.groups.other'),
      vars: [
        { name: '{{phone_date}}', desc: t('emailTemplate.form.vars.phoneDate') },
        { name: '{{phone_time}}', desc: t('emailTemplate.form.vars.phoneTime') },
        { name: '{{salary}}', desc: t('emailTemplate.form.vars.salary') },
        { name: '{{start_date}}', desc: t('emailTemplate.form.vars.startDate') },
        { name: '{{probation_duration}}', desc: t('emailTemplate.form.vars.probationDuration') },
        { name: '{{manager_name}}', desc: t('emailTemplate.form.vars.managerName') },
      ],
    },
  ];
  const templateCategories = [
    t('emailTemplate.form.categories.recruitment'),
    t('emailTemplate.form.categories.internalProcess'),
    t('emailTemplate.form.categories.externalCommunication'),
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('emailTemplate.form.createTitle') : t('emailTemplate.form.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">{t('emailTemplate.form.nameLabel')}</Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('emailTemplate.form.namePlaceholder')}
                disabled={isSubmitting}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template-category">{t('emailTemplate.form.categoryLabel')}</Label>
              <select
                id="template-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={isSubmitting}
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">{t('emailTemplate.form.categoryPlaceholder')}</option>
                {templateCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-subject">{t('emailTemplate.form.subjectLabel')}</Label>
            <Input
              id="template-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('emailTemplate.form.subjectPlaceholder')}
              disabled={isSubmitting}
            />
          </div>

          {/* Body HTML with Edit/Preview tabs */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('emailTemplate.form.bodyLabel')}</Label>
              <div className="flex rounded-md border overflow-hidden text-sm">
                <button
                  type="button"
                  onClick={() => setBodyTab('edit')}
                  className={`px-3 py-1 transition-colors ${
                    bodyTab === 'edit'
                      ? 'bg-orange-50 text-orange-700 font-medium'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {t('emailTemplate.form.tabEdit')}
                </button>
                <button
                  type="button"
                  onClick={() => setBodyTab('preview')}
                  className={`px-3 py-1 border-l transition-colors ${
                    bodyTab === 'preview'
                      ? 'bg-orange-50 text-orange-700 font-medium'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {t('emailTemplate.form.tabPreview')}
                </button>
              </div>
            </div>

            {bodyTab === 'edit' ? (
              <TiptapEmailEditor
                value={bodyHtml}
                onChange={setBodyHtml}
                disabled={isSubmitting}
                placeholder={t('emailTemplate.form.bodyPlaceholder')}
              />
            ) : (
              <div className="border rounded-md p-4 bg-white min-h-[200px] max-h-[400px] overflow-y-auto">
                {bodyHtml ? (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyHtml) }}
                  />
                ) : (
                  <p className="text-sm text-gray-400 italic">{t('emailTemplate.form.noPreview')}</p>
                )}
              </div>
            )}
          </div>

          {/* Variable Reference */}
          <div className="border rounded-md">
            <button
              type="button"
              onClick={() => setShowVariableRef(!showVariableRef)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {showVariableRef ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              {t('emailTemplate.form.variableReference')}
            </button>
            {showVariableRef && (
              <div className="px-3 pb-3 grid grid-cols-2 gap-x-6 gap-y-1">
                {variableGroups.map((group) => (
                  <div key={group.title}>
                    <p className="text-xs font-medium text-gray-500 mt-2 mb-1">{group.title}</p>
                    {group.vars.map((v) => (
                      <div key={v.name} className="flex items-center gap-2 text-xs py-0.5">
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-orange-600">
                          {v.name}
                        </code>
                        <span className="text-gray-500">{v.desc}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? t('common.creating') : t('common.saving')}
                </>
              ) : mode === 'create' ? (
                t('emailTemplate.form.createButton')
              ) : (
                t('common.saveChanges')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
